import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Autoplay } from 'swiper/modules';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { callPhone } from '../../utils/phoneUtils';
import type { ServiceCarouselProps } from './types';

const ServiceCarousel: React.FC<ServiceCarouselProps> = ({ data, onBookClick }) => {
  const navigate = useNavigate();
  const swiperRef = useRef<SwiperType | null>(null);
  const allServices = data.categories.flatMap(cat => cat.services);

  return (
    <section className="py-16 bg-white dark:bg-custom-dark overflow-hidden">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 mb-12 text-center">
        <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
          {data.title}
        </h2>
        <p className="text-xl text-gray-600 dark:text-gray-300 mb-4">{data.subtitle}</p>
        
        {/* View More Link - After Description, Right-Aligned, No Background */}
        <div className="flex justify-end">
          <button
            onClick={() => navigate('/services-v2')}
            className="text-primary hover:text-primary/80 font-semibold flex items-center gap-2 transition-colors text-base md:text-lg"
          >
            <span>View More</span>
            <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Service Cards Carousel with Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative">
        {/* Custom Previous Button */}
        <button
          onClick={() => swiperRef.current?.slidePrev()}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center transition-all shadow-lg"
          aria-label="Previous slide"
        >
          <IoChevronBack className="text-white text-2xl" />
        </button>

        {/* Custom Next Button */}
        <button
          onClick={() => swiperRef.current?.slideNext()}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center transition-all shadow-lg"
          aria-label="Next slide"
        >
          <IoChevronForward className="text-white text-2xl" />
        </button>

        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          modules={[Autoplay]}
          spaceBetween={24}
          slidesPerView={1}
          breakpoints={{
            640: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 24,
            },
            1280: {
              slidesPerView: 4,
              spaceBetween: 24,
            },
          }}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
          }}
          loop={true}
          className="service-carousel-desktop"
        >
          {allServices.map((service, index) => (
            <SwiperSlide key={index}>
              <div className="bg-[#252527] rounded-xl shadow-lg overflow-hidden flex flex-col h-full min-h-[280px] transition-transform hover:scale-105">
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-xl font-bold text-white mb-3">
                    {service.name}
                  </h3>
                  
                  <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                    {service.description}
                  </p>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-2xl font-bold text-primary">
                      {service.price}
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm">{service.duration}</span>
                    </div>
                  </div>
                </div>
                
                {/* Book Now Button at Bottom */}
                <button
                  onClick={onBookClick || (() => callPhone())}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-4 px-6 flex items-center justify-center gap-3 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>Book Now</span>
                </button>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default ServiceCarousel;
